import { useId, useMemo, useState } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts'
import useTranslation from '../../../../hooks/useTranslation'
import AnalyticsCard from './AnalyticsCard'
import { Headline, KpiRow, formatOf } from './Kpi'
import { ChartBox, Legend, TooltipCard, axisProps } from './ChartParts'
import { bucketLabel, spansYears } from './period'
import { measuresOf } from './measures'
import useAnalyticsWidget from './useAnalyticsWidget'
import useElementSize from './useElementSize'
import { chartFont } from '../../widgetTypography'

/*
 * One metric over time (Area), or two on the same axis (Double Area): a line
 * with a translucent fill down to the baseline, the headline above, KPIs below.
 *
 *     series: [{ id, label, value: <expression>, color, format }]
 *     showComparison   draws the previous period as a dashed line under the
 *                      first series, aligned bucket for bucket
 *     target           { value, label } — a reference line
 *     missing          'zero' (default) — a bucket without rows is 0;
 *                      'gap' — it is left out and the line breaks there
 *     curve            'monotone' (default) or 'linear'
 *
 * The two series of a double area are independent values, never stacked; each
 * fill is light enough that the one behind shows through. The legend hides one.
 *
 * In a card too small for axes the chart becomes a sparkline: the line and the
 * tooltip, nothing else.
 */
const AreaChartWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const w = useAnalyticsWidget(type, config)
    const { period, data, palette, format, locale } = w
    const [ref, size] = useElementSize()
    const [hidden, setHidden] = useState([])
    const uid = useId().replace(/[^a-zA-Z0-9]/g, '')

    const series = useMemo(() => (config.series ?? []).map((s, i) => ({
        ...s,
        name: t(s.label),
        color: palette.color(s.color, i),
        format: s.format ?? formatOf(s.value, config.measures),
    })), [config.series, config.measures, palette, t])

    const compare = config.showComparison && data.previous && series.length === 1
    const multi = series.length > 1

    const rows = useMemo(() => {
        if (!data.current) return []
        const yearSpan = spansYears(period.range)
        const values = series.map(s => data.current.series(s.value))
        const present = config.missing === 'gap'
            ? series.map(s => period.keys.map(k => measuresOf(s.value).some(id => data.current.indexes[id]?.byBucket.has(k))))
            : null
        const prev = compare ? data.previous.series(series[0].value) : null
        return period.keys.map((key, i) => {
            const row = { key, label: bucketLabel(key, period.grain, locale, { spansYears: yearSpan }) }
            series.forEach((s, j) => { row[s.id] = present && !present[j][i] ? null : values[j][i] })
            if (prev) {
                row.__prev = prev[i] ?? null
                row.__prevKey = period.previousKeys[i]
            }
            return row
        })
    }, [data.current, data.previous, series, period, locale, compare, config.missing])

    const sparkline = size.height < 110 || size.width < 220
    const showYAxis = !sparkline && size.width >= 340
    const curve = config.curve === 'linear' ? 'linear' : 'monotone'

    const renderTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null
        const row = payload[0].payload
        const items = series.filter(s => !hidden.includes(s.id)).map(s => ({ color: s.color, name: s.name, value: format(row[s.id], s.format) }))
        if (compare && row.__prevKey) {
            items.push({ color: palette.axis, dashed: true, name: bucketLabel(row.__prevKey, period.grain, locale, { long: true }), value: format(row.__prev, series[0].format) })
        }
        return <TooltipCard title={bucketLabel(row.key, period.grain, locale, { long: true }) ?? label} rows={items} />
    }

    const legend = multi && (
        <Legend
            items={series.map(s => ({ id: s.id, name: s.name, color: s.color }))}
            hidden={hidden}
            onToggle={id => setHidden(h => h.includes(id) ? h.filter(x => x !== id) : [...h, id])}
        />
    )

    return (
        <AnalyticsCard
            labels={labels}
            period={period}
            status={w.status}
            error={data.error}
            onRetry={data.refetch}
            headline={<Headline kpi={w.headline} formatValue={format} compareLabel={w.compareLabel} />}
            footer={<KpiRow kpis={w.kpis} formatValue={format} />}
        >
            {legend}
            <ChartBox>
                <div ref={ref}>
                    {size.width > 0 && size.height > 0 && (
                        <AreaChart width={size.width} height={size.height} data={rows} accessibilityLayer
                            margin={{ top: 6, right: 4, left: showYAxis ? 0 : 4, bottom: 0 }}>
                            <defs>
                                {series.map(s => (
                                    <linearGradient key={s.id} id={`${uid}-${s.id}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={s.color} stopOpacity={multi ? 0.22 : 0.3} />
                                        <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                                    </linearGradient>
                                ))}
                            </defs>
                            {!sparkline && <CartesianGrid vertical={false} stroke={palette.grid} strokeDasharray="3 3" />}
                            <XAxis dataKey="label" hide={sparkline} {...axisProps(palette)} minTickGap={18} interval="preserveStartEnd" />
                            <YAxis hide={!showYAxis} width={52} {...axisProps(palette)}
                                tickFormatter={v => format(v, series[0]?.format, { compact: true })}
                                domain={config.domain ?? [0, 'auto']} allowDataOverflow={false} />
                            <Tooltip content={renderTooltip} cursor={{ stroke: palette.axis, strokeDasharray: '3 3' }} />
                            {config.target && (
                                <ReferenceLine y={config.target.value} stroke={palette.tokens.neutral} strokeDasharray="4 4"
                                    label={sparkline ? undefined : { value: t(config.target.label), position: 'insideTopRight', fontSize: chartFont.label, fill: palette.axis }} />
                            )}
                            {compare && (
                                <Area type={curve} dataKey="__prev" name="previous" stroke={palette.axis} strokeWidth={1.5}
                                    strokeDasharray="4 4" fill="none" dot={false} activeDot={false} isAnimationActive={false} />
                            )}
                            {series.filter(s => !hidden.includes(s.id)).map(s => (
                                <Area key={s.id} type={curve} dataKey={s.id} name={s.name}
                                    stroke={s.color} strokeWidth={2} fill={`url(#${uid}-${s.id})`}
                                    dot={config.markers && !sparkline ? { r: 2.5, strokeWidth: 0, fill: s.color } : false}
                                    activeDot={{ r: 4, strokeWidth: 2, stroke: palette.surface }}
                                    connectNulls={false} isAnimationActive={false} />
                            ))}
                        </AreaChart>
                    )}
                </div>
            </ChartBox>
        </AnalyticsCard>
    )
}

export default AreaChartWidget
